<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <Navbar />
    <div class="feed-page">
      <!-- Création de post -->
      <el-card v-if="isLoggedIn" class="create-card" shadow="always">
        <el-input
          v-model="postContent"
          type="textarea"
          :rows="3"
          placeholder="Saisissez ici du texte..."
        />
        <div class="create-actions">
          <el-upload
            action=""
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleImageChange"
          >
            <el-button type="primary" link>
              <el-icon><Picture /></el-icon> Insérer une image
            </el-button>
          </el-upload>
          <span v-if="postImageName" class="file-name">{{ postImageName }}</span>
          <el-button
            type="primary"
            :disabled="!postContent.trim()"
            @click="createPost"
          >
            Poster
          </el-button>
        </div>
      </el-card>

      <el-alert
        v-else
        title="Connectez-vous pour créer des posts !"
        type="info"
        show-icon
        :closable="false"
        class="login-alert"
      />

      <!-- Liste des posts -->
      <div v-for="post in sortedPosts" :key="post.id">
        <el-card class="post-card" shadow="always">
          <template #header>
            <div class="post-header-el">
              <el-avatar :size="40" :src="getProfilePictureUrl(post.User.profile_picture)" />
              <div class="post-meta">
                <span class="username"><UserLink :username="post.User && post.User.username" /></span>
                <span class="date">{{ formatDate(post.createdAt) }}</span>
              </div>
            </div>
          </template>

          <p class="post-text">{{ post.content }}</p>
          <el-image
            v-if="post.imageUrl"
            :src="getPostImageUrl(post.imageUrl)"
            fit="cover"
            class="post-image-el"
            :preview-src-list="[getPostImageUrl(post.imageUrl)]"
          />

          <template #footer>
            <div class="post-footer-el">
              <el-tag type="info" size="small">❤️ {{ post.likeCount }}</el-tag>
              <div class="post-actions">
                <el-button
                  v-if="isLoggedIn"
                  type="primary"
                  size="small"
                  @click="likePost(post.id)"
                >
                  J'aime
                </el-button>
                <el-button
                  v-if="isLoggedIn"
                  size="small"
                  @click="removeLike(post.id)"
                >
                  Je n'aime plus
                </el-button>
                <el-button
                  v-if="isLoggedIn"
                  size="small"
                  @click="commentPost(post)"
                >
                  Commenter
                </el-button>
                <el-button size="small" @click="viewPost(post)">
                  Voir le post
                </el-button>
                <el-button
                  v-if="isLoggedIn && post.User.id === userId"
                  type="danger"
                  size="small"
                  @click="deletePost(post.id)"
                >
                  Supprimer
                </el-button>
                <ReportButton
                  v-if="isLoggedIn && post.User && post.User.id !== userId"
                  target-type="post"
                  :target-id="post.id"
                />
              </div>
            </div>
          </template>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import UserLink from '@/components/UserLink.vue'
import ReportButton from '@/components/ReportButton.vue'
import api, { API_BASE_URL } from '@/api';
import { Picture } from '@element-plus/icons-vue'
import { notifySuccess, notifyError, confirmAction } from '@/notify'

export default {
  components: {
    Navbar,
    UserLink,
    ReportButton,
    Picture,
  },

  data() {
    return {
      userId: null,
      posts: [],
      postContent: '',
      postImageFile: null,
      postImageName: '',
      selectedInterest: null,
      isLoggedIn: false,
    }
  },

  computed: {
    sortedPosts() {
      return this.posts.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
  },

  mounted() {
    this.checkUserSession()
    this.fetchAllPosts()
    document.body.style.backgroundColor = 'rgba(21, 109, 123)'
  },

  methods: {
    viewPost(post) {
      this.$router.push({ name: 'Post', params: { postId: post.id } })
    },

    // Ouvre le post avec le champ de commentaire directement sélectionné
    commentPost(post) {
      this.$router.push({ name: 'Post', params: { postId: post.id }, query: { comment: '1' } })
    },

    async checkUserSession() {
      try {
        // Un seul appel /session/me : il renseigne à la fois la connexion et l'id de l'utilisateur
        const response = await api.get('/session/me', { withCredentials: true })
        this.isLoggedIn = response.status === 200
        if (this.isLoggedIn) this.userId = response.data.id
      } catch (error) {
        console.error('Error checking session:', error)
        this.isLoggedIn = false
      }
    },

    async fetchAllPosts() {
      try {
        const response = await api.get('/post/chippies')
        this.posts = response.data.posts
      } catch (error) {
        console.error('Error fetching all posts:', error)
      }
    },

    handleImageChange(file) {
      this.postImageFile = file.raw
      this.postImageName = file.name
    },

    createPost() {
      const formData = new FormData()
      formData.append('content', this.postContent)
      formData.append('type', 'CHIPPIES')
      if (this.postImageFile) {
        formData.append('postPicture', this.postImageFile)
      }

      api
        .post('/post/create', formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(() => {
          this.postContent = ''
          this.postImageFile = null
          this.postImageName = ''
          this.selectedInterest = null
          notifySuccess('Publication créée !')
          this.fetchAllPosts()
          this.resetPostDraft()
        })
        .catch((error) => {
          console.error('Error creating post:', error)
          notifyError(error, 'Erreur lors de la publication')
        })
    },

    getProfilePictureUrl(profilePicturePath) {
      return `${API_BASE_URL}/${profilePicturePath}`
    },

    getPostImageUrl(imagePath) {
      return `${API_BASE_URL}/${imagePath}`
    },

    async likePost(postId) {
      try {
        await api.post(`/post/${postId}/like`, {}, { withCredentials: true })
        this.fetchAllPosts()
      } catch (error) {
        console.error('Error liking post:', error)
        notifyError(error, 'Impossible d\'aimer ce post')
      }
    },

    async removeLike(postId) {
      try {
        const response = await api.delete(`/post/removeLike/${postId}`, { withCredentials: true })
        if (response.status === 200) {
          this.fetchAllPosts()
        } else {
          console.error('Error removing like:', response.data.message)
        }
      } catch (error) {
        console.error('Error removing like:', error)
        notifyError(error, 'Impossible de retirer le j\'aime')
      }
    },

    formatDate(dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' }
      return new Date(dateString).toLocaleDateString('fr-FR', options)
    },

    // Vide le brouillon de publication après envoi
    resetPostDraft() {
      this.postContent = ''
      this.selectedInterest = null
    },

    async deletePost(postId) {
      if (!(await confirmAction('Supprimer ce post ?'))) return
      try {
        const response = await api.delete(`/post/delete/${postId}`, { withCredentials: true })
        if (response.status === 200) {
          notifySuccess('Post supprimé')
          this.fetchAllPosts()
        } else console.error('Error deleting post:', response.data.message)
      } catch (error) {
        console.error('Error deleting post:', error)
        notifyError(error, 'Erreur lors de la suppression')
      }
    },
  },
}
</script>

<style scoped>
.feed-page {
  padding: 70px 20px 20px;
  max-width: 700px;
  margin: 0 auto;
}

.create-card {
  margin-bottom: 20px;
}

.create-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.file-name {
  color: #606266;
  font-size: 13px;
  flex-grow: 1;
}

.login-alert {
  max-width: 700px;
  margin: 0 auto 20px;
}

.post-card {
  margin-bottom: 20px;
}

.post-header-el {
  display: flex;
  align-items: center;
  gap: 12px;
}

.post-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.post-meta .username {
  font-weight: 600;
  color: #303133;
  cursor: pointer;
}

.post-meta .date {
  font-size: 12px;
  color: #909399;
}

.post-text {
  margin: 0;
  line-height: 1.5;
  color: #303133;
}

.post-image-el {
  max-height: 300px;
  border-radius: 8px;
  margin-top: 12px;
}

.post-footer-el {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.post-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
